import type { InviteeInput } from '@sportninja/common/services/teamBalance'
import type {
  InviteFormErrors,
  InviteFormMessages,
  InviteFormValues,
  InviteRow,
  InviteRowErrors,
} from '../types/slotInvite'

/*
 * Jeff, Oct 1 — the invite form's rules, kept out of the modals so they are tested
 * on their own. Every form edits a list of rows: Add Player Slots › Reserved Player
 * Slots up to MAX_INVITEES, `slot` (⋮ Invite Player) and `change` (Change Invitee)
 * exactly one.
 */

/** The server accepts at most this many invitees per submit. */
export const MAX_INVITEES = 20

/** Matches the server's limits (name 50, email 100). */
export const NAME_MAX_LENGTH = 50
export const EMAIL_MAX_LENGTH = 100

export const emptyInviteRow = (): InviteRow => ({
  name_first: '',
  name_last: '',
  email: '',
})

/** The form's starting rows: one empty row, or the invitee being changed. */
export const initialInviteValues = (
  initial?: Partial<InviteRow> | null
): InviteFormValues => ({
  invitees: [
    {
      name_first: initial?.name_first ?? '',
      name_last: initial?.name_last ?? '',
      email: initial?.email ?? '',
    },
  ],
})

/**
 * The same shape check the server's EmailAddress rule makes: a syntactically valid
 * address with a dotted top-level domain. The server stays authoritative.
 */
export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())

/** One person: the same email AND name. Siblings sharing a parent's email differ. */
const personKey = (row: InviteRow): string =>
  [row.email, row.name_first, row.name_last]
    .map((part) => part.trim().toLowerCase())
    .join('\u0000')

/**
 * Field errors for the rows, in Formik's shape — or `{}` when the form can be sent.
 * A repeated person is flagged on its second row, as the server flags it.
 */
export const validateInviteRows = (
  values: InviteFormValues,
  messages: InviteFormMessages
): InviteFormErrors => {
  const seen = new Set<string>()
  let hasErrors = false

  const rowErrors = values.invitees.map((row) => {
    const errors: InviteRowErrors = {}
    if (!row.name_first.trim()) {
      errors.name_first = messages.firstNameRequired
    }
    if (!row.name_last.trim()) {
      errors.name_last = messages.lastNameRequired
    }
    if (!row.email.trim()) {
      errors.email = messages.emailRequired
    } else if (!isValidEmail(row.email)) {
      errors.email = messages.emailInvalid
    }

    if (!errors.email && row.email.trim()) {
      const key = personKey(row)
      if (seen.has(key)) {
        errors.email = messages.duplicate
      }
      seen.add(key)
    }

    if (Object.keys(errors).length > 0) {
      hasErrors = true
    }

    return errors
  })

  return hasErrors ? { invitees: rowErrors } : {}
}

/** The rows as the API takes them: trimmed, the email lower-cased. */
export const toInvitees = (values: InviteFormValues): InviteeInput[] =>
  values.invitees.map((row) => ({
    name_first: row.name_first.trim(),
    name_last: row.name_last.trim(),
    email: row.email.trim().toLowerCase(),
  }))

const INVITEE_FIELDS: ReadonlyArray<keyof InviteRow> = [
  'name_first',
  'name_last',
  'email',
]

/**
 * The server's 422 mapped onto the form: `invitees.N.field` (Reserved Player
 * Slots) or a bare `field` (one invitee) become the matching row's field error;
 * anything else (the budget is locked, the team isn't approved, the slot changed)
 * is a form-level message the caller shows on its own.
 */
export const mapInviteServerErrors = (
  invalidFields: Record<string, unknown> | null | undefined
): { fieldErrors: Record<string, string>; formMessage: string | null } => {
  const fieldErrors: Record<string, string> = {}
  let formMessage: string | null = null

  for (const [key, value] of Object.entries(invalidFields ?? {})) {
    const message = Array.isArray(value)
      ? String(value[0] ?? '')
      : String(value ?? '')
    if (!message) {
      continue
    }

    const rowMatch = /^invitees\.(\d+)\.(name_first|name_last|email)$/.exec(key)
    if (rowMatch) {
      fieldErrors[`invitees.${rowMatch[1]}.${rowMatch[2]}`] = message
    } else if ((INVITEE_FIELDS as readonly string[]).includes(key)) {
      fieldErrors[`invitees.0.${key}`] = message
    } else if (formMessage === null) {
      formMessage = message
    }
  }

  return { fieldErrors, formMessage }
}
